/**
 * A Block View that wraps the VideoJS Block View and adds media-page specific
 * functionality: source derivation, processing/progress feedback, and live.
 *
 * @module components/manage/Blocks/MediaPageVideoJS
 */

import React from 'react';
import { Progress, Loader, Header, Message } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { useIntl, defineMessages } from 'react-intl';
import { toast } from 'react-toastify';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import LiveAwareVideoPlayer from 'volto-base-addon/components/manage/Blocks/VideoJS/LiveAwareVideoPlayer';
import { useMediaPlayerSources } from 'volto-base-addon/components/manage/Blocks/VideoJS/useMediaPlayerSources';
import { useMediaProcessing } from 'volto-base-addon/components/manage/Blocks/VideoJS/useMediaProcessing';
import {
  useLiveStream,
  useLiveWorkflowState,
} from 'volto-base-addon/components/manage/Blocks/VideoJS/useLiveStream';
import {
  deriveFailedItems,
  deriveNoSourceComing,
  isTerminalState,
  outputStateOf,
} from 'volto-base-addon/helpers/mediaProcessingLogic';
import config from '@plone/volto/registry';

const messages = defineMessages({
  subtitlesProcessing: {
    id: 'subtitlesProcessing',
    defaultMessage: 'Generating subtitles…',
  },
  subtitlesTranslation: {
    id: 'subtitlesTranslation',
    defaultMessage: 'Translating subtitles to English…',
  },
  pleaseWait: {
    id: 'mediaPagePleaseWait',
    defaultMessage: 'Loading…',
  },
  applyingFaceBlur: {
    id: 'applyingFaceBlur',
    defaultMessage: 'Applying face blur…',
  },
  processingProblems: {
    id: 'processingProblems',
    defaultMessage: 'Some processing did not complete',
  },
  transcodingFailedSummary: {
    id: 'transcodingFailedSummary',
    defaultMessage:
      '{count, plural, one {One video quality} other {{count} video qualities}} could not be generated.',
  },
  subtitleFailedSummary: {
    id: 'subtitleFailedSummary',
    defaultMessage:
      '{count, plural, one {One subtitle} other {{count} subtitles}} could not be generated. You can try again from the subtitle menu.',
  },
  videoCurrentlyUnavailable: {
    id: 'videoCurrentlyUnavailable',
    defaultMessage:
      'The video is currently unavailable. Retrying automatically…',
  },
  videoProcessingFailed: {
    id: 'videoProcessingFailed',
    defaultMessage: 'This video could not be processed.',
  },
  generationMayHaveFailed: {
    id: 'generationMayHaveFailed',
    defaultMessage:
      'Subtitle generation may have failed. Please try again from the subtitle menu.',
  },
  transcodingComplete: {
    id: 'transcodingComplete',
    defaultMessage: 'Video processing complete.',
  },
  subtitlesReady: {
    id: 'subtitlesReady',
    defaultMessage: 'Subtitles are ready.',
  },
});

// These are the profiles that the block will try query about
// (they also define the order in which the elements will be rendered)
const profiles = ['Audio', '360p', '720p', '1080p', '2160p'];

// This probably should not be the master of this information
const PROFILE_TITLES = {
  Audio: 'Audio',
  '360p': '360p',
  '720p': '720p',
  '1080p': '1080p',
  '2160p': '2160p',
};

/**
 * Compact, non-alarming notice for failed processing items.
 * Differentiates transcodings (more serious) from subtitles (recoverable).
 */
const FailedItemsNotice = ({ failedItems, intl }) => {
  const [expanded, setExpanded] = React.useState(false);
  const failedTranscodings = failedItems.filter(
    (i) => i['@type'] === 'transcoding',
  );
  const failedSubtitles = failedItems.filter((i) => i['@type'] === 'subtitle');

  return (
    <div className="mediapage-failed-items" role="status">
      <button
        type="button"
        className="mediapage-failed-items-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span>{intl.formatMessage(messages.processingProblems)}</span>
        <span className="mediapage-failed-items-toggle">
          {expanded ? '▴' : '▾'}
        </span>
      </button>
      {expanded && (
        <div className="mediapage-failed-items-details">
          {failedTranscodings.length > 0 && (
            <p>
              {intl.formatMessage(messages.transcodingFailedSummary, {
                count: failedTranscodings.length,
              })}{' '}
              ({failedTranscodings.map((i) => i.title).join(', ')})
            </p>
          )}
          {failedSubtitles.length > 0 && (
            <p>
              {intl.formatMessage(messages.subtitleFailedSummary, {
                count: failedSubtitles.length,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const View = (props) => {
  const intl = useIntl();
  const { data, className } = props;

  // content can be momentarily empty during navigation. The global content
  // (fetched by core View.jsx at mount) provides identity.
  const content = useSelector((state) => state.content.data) || {};
  const actions = useSelector((state) => state.actions?.actions?.object);
  const isEditor =
    Array.isArray(actions) && actions.some((a) => a.id === 'edit');

  const contentPath = content['@id'] ? flattenToAppURL(content['@id']) : null;

  // Enriched content comes from MediaPageView (content-type view) via Redux.
  // It includes items[].output_state, subtitle_*, s3_object_id, file_size.
  const enrichedContent = useSelector(
    (state) => state.mediaPageEnriched?.content,
  );
  // True while the freshness engine is in an active processing session
  // (upload redirect or a subtitle generation the user just started).
  const isPolling = useSelector((state) => state.mediaPageEnriched?.isPolling);

  // Layer 2: Pure observation (reads enriched content, writes nothing back).
  const isLiveWorkflowActive = useLiveWorkflowState(enrichedContent);
  const live = useLiveStream(enrichedContent, isLiveWorkflowActive);
  const { playerData, playerState } = useMediaPlayerSources(enrichedContent);
  const {
    transcodingProgress,
    transcriptionEntries,
    faceblurProgress,
    generationStatus,
  } = useMediaProcessing(enrichedContent, {
    isLiveStream: live.isLiveStream,
  });

  // Content-state facts, derived directly from output_state metadata — no
  // Camunda data, no @workflow calls. Available as soon as content is enriched.
  const enrichedItems = Array.isArray(enrichedContent?.items)
    ? enrichedContent.items
    : [];
  const failedItems = deriveFailedItems(enrichedItems);
  const noSourceComing = deriveNoSourceComing({
    items: enrichedItems,
    hasPlayableSource: !!enrichedContent?.s3_object_id,
    isLiveStream: live.isLiveStream,
  });

  // A child is genuinely in flight when it exists but is not yet terminal, and
  // we are in an active processing session. This keeps the throbber up across
  // the gap where the generation signal has self-cleared but Camunda emits no
  // progress bars (e.g. a very fast transcription) — until the completion toast.
  const nonTerminalChildren = enrichedItems.filter(
    (i) =>
      (i['@type'] === 'subtitle' || i['@type'] === 'transcoding') &&
      !isTerminalState(outputStateOf(i)),
  );
  const hasActiveChild = !!isPolling && nonTerminalChildren.length > 0;
  const hasPendingSubtitle = nonTerminalChildren.some(
    (i) => i['@type'] === 'subtitle',
  );

  // Completion toast: track items seen as non-terminal ("waiting").
  // When a waiting item transitions to completed → toast.
  // Items first seen as already completed are never toasted.
  const waitingRef = React.useRef(new Set());
  const toastedRef = React.useRef(new Set());
  React.useEffect(() => {
    if (!isEditor) return;

    for (const item of enrichedItems) {
      if (item['@type'] !== 'transcoding' && item['@type'] !== 'subtitle')
        continue;
      const id = item['@id'];
      if (!id) continue;
      const state = outputStateOf(item);
      if (!state) continue;

      if (state !== 'completed' && state !== 'failed') {
        // Non-terminal: mark as waiting
        waitingRef.current.add(id);
      } else if (
        state === 'completed' &&
        waitingRef.current.has(id) &&
        !toastedRef.current.has(id)
      ) {
        // Was waiting, now completed: toast (unique toastId per item)
        toastedRef.current.add(id);
        const isSubtitle = item['@type'] === 'subtitle';
        toast.success(
          <Toast
            success
            title={intl.formatMessage(
              isSubtitle
                ? messages.subtitlesReady
                : messages.transcodingComplete,
            )}
            content=""
          />,
          { toastId: `completion-${id}` },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichedContent]);

  let modifiedPlayerData = null;
  if (playerState === 'INITIALIZED') {
    modifiedPlayerData = {
      ...data,
      options: {
        ...data.options,
        ...playerData.data,
      },
    };
    live.applyLiveOptions(modifiedPlayerData.options);
  }

  const LiveChatComponent = config.getComponent({
    name: 'LiveChat',
  })?.component;
  const showLiveChat =
    live.isLiveStream && LiveChatComponent && content?.chat_enabled !== false;

  // Parse ?t= time parameter for seeking on ready (e.g. ?t=1m30s or ?t=90)
  const startTime = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    const t = new URLSearchParams(window.location.search).get('t');
    if (!t) return null;
    if (/[hms]/.test(t)) {
      const h = t.match(/(\d+)h/);
      const m = t.match(/(\d+)m/);
      const s = t.match(/(\d+)s/);
      return (
        (h ? parseInt(h[1], 10) : 0) * 3600 +
        (m ? parseInt(m[1], 10) : 0) * 60 +
        (s ? parseInt(s[1], 10) : 0)
      );
    }
    return parseInt(t, 10) || 0;
  }, []);

  const videoArea = (
    <LiveAwareVideoPlayer
      live={live}
      playerData={modifiedPlayerData}
      bodyProps={{
        ...props,
        parentContent: content,
        parentUid: props.properties?.UID,
        parentItems: enrichedContent?.items || props.properties?.items,
        startTime,
        editMode: false,
      }}
    />
  );

  // One processing region: a spinner until concrete progress, then the bars.
  // Also shown next to a playing video while something still runs (later
  // rendition / Path B subtitles).
  const playerVisible = !!modifiedPlayerData;
  const hasTranscodingBars =
    !!transcodingProgress && Object.keys(transcodingProgress).length > 0;
  const hasTranscriptionBars =
    transcriptionEntries && transcriptionEntries.length > 0;
  const hasFaceblurBar = !!(faceblurProgress && !faceblurProgress.complete);
  const hasProgressBars =
    hasTranscodingBars || hasTranscriptionBars || hasFaceblurBar;
  const showProcessing =
    !live.isLiveStream &&
    playerState !== 'FAILED' &&
    !noSourceComing &&
    (!playerVisible ||
      hasProgressBars ||
      generationStatus === 'pending' ||
      hasActiveChild);

  return (
    <div
      className={cx(
        'block video align',
        {
          center: !Boolean(data.align),
        },
        data.align,
        className,
      )}
    >
      {showLiveChat ? (
        <div className="live-chat-layout">
          <div className="live-chat-video">{videoArea}</div>
          <LiveChatComponent contentPath={contentPath} />
        </div>
      ) : (
        videoArea
      )}
      {showProcessing ? (
        <div className="mediapage-processing">
          {!hasProgressBars ? (
            // role="status": the only thing on screen while we wait, and the
            // text switches as processing moves on.
            <div className="mediapage-before-progress" role="status">
              <Loader active inline>
                {generationStatus === 'pending' || hasPendingSubtitle
                  ? intl.formatMessage(messages.subtitlesProcessing)
                  : intl.formatMessage(messages.pleaseWait)}
              </Loader>
            </div>
          ) : (
            // Semantic's Progress renders no ARIA of its own, so every bar has
            // to carry role/value/name itself or it is invisible to assistive
            // tech. Deliberately not a live region: a bar that announced every
            // percentage tick would be unusable.
            <>
              {hasFaceblurBar ? (
                <div className="mediapage-faceblur-progress-indicator">
                  <Header as="h4">
                    {intl.formatMessage(messages.applyingFaceBlur)}
                  </Header>
                  <Progress
                    percent={faceblurProgress.percentage}
                    indicating
                    progress
                    role="progressbar"
                    aria-label={intl.formatMessage(messages.applyingFaceBlur)}
                    aria-valuenow={faceblurProgress.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              ) : null}
              {hasTranscriptionBars ? (
                <div className="mediapage-transcription-progress-indicator">
                  {transcriptionEntries.map((entry, idx) => {
                    const label =
                      entry.task === 'translate'
                        ? intl.formatMessage(messages.subtitlesTranslation)
                        : intl.formatMessage(messages.subtitlesProcessing);
                    return (
                      <div key={`${entry.task}-${idx}`}>
                        <div>{label}</div>
                        <Progress
                          percent={entry.percentage}
                          indicating
                          progress
                          role="progressbar"
                          aria-label={label}
                          aria-valuenow={entry.percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {hasTranscodingBars ? (
                <div className="mediapage-transcoding-progress-indicator">
                  {profiles.map((profile) =>
                    transcodingProgress?.[profile] ? (
                      <div key={profile}>
                        <div> {PROFILE_TITLES[profile]} </div>
                        <Progress
                          percent={transcodingProgress[profile]}
                          indicating
                          progress
                          role="progressbar"
                          aria-label={PROFILE_TITLES[profile]}
                          aria-valuenow={transcodingProgress[profile]}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    ) : null,
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
      {/* Stream not resolved yet — the source hook keeps retrying. */}
      {playerState === 'FAILED' && !live.isLiveStream && !noSourceComing ? (
        <Message
          info
          className="mediapage-video-unavailable"
          role="status"
          content={intl.formatMessage(messages.videoCurrentlyUnavailable)}
        />
      ) : null}
      {/* No source can come (every transcoding failed). */}
      {noSourceComing && !modifiedPlayerData ? (
        <Message
          warning
          className="mediapage-video-failed"
          role="status"
          content={intl.formatMessage(messages.videoProcessingFailed)}
        />
      ) : null}
      {isEditor && failedItems.length > 0 && (
        <div className="mediapage-failed-items-wrapper">
          <FailedItemsNotice failedItems={failedItems} intl={intl} />
        </div>
      )}
      {/* Generation started but nothing appeared within the timeout. */}
      {isEditor && generationStatus === 'timed_out' ? (
        <Message
          warning
          className="mediapage-generation-timeout"
          role="status"
          content={intl.formatMessage(messages.generationMayHaveFailed)}
        />
      ) : null}
    </div>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
View.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withBlockExtensions(View);
